import {Component, OnInit}                  from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MatDialog, MatDialogRef}            from '@angular/material/dialog';
import {MatSnackBar}                        from '@angular/material/snack-bar';
import {MatTableDataSource}                 from '@angular/material/table';
import {ActivatedRoute, Router}             from '@angular/router';
import {PriceUploadModalComponent}          from '@src/app/admin/products/components/price-upload-modal/price-upload-modal.component';
import {ProductManufacturer}                from '@src/app/admin/products/models/product.model';
import {Breadcrumb}                         from '@src/app/shared/models/breadcrumb.model';
import {Button}                             from '@src/app/shared/models/button.model';
import {ApiService}                         from '@src/app/shared/services/api.service';
import {SettingsService}                    from '@src/app/core/services/settings.service';
import {query}                              from 'js-query-builder';
import {DialogComponent}                    from '@src/app/shared/components/dialog/dialog.component';

@Component({
    standalone: false,
    selector: 'app-create',
    templateUrl: './products-form.component.html',
    styleUrls: ['./products-form.component.css']
})
export class ProductsFormComponent implements OnInit
{
    public readonly pricingDisplayedColumns: string[] = ['manufacturer', 'specification_sheet_count', 'operative_cost', 'subcontractor_cost', 'material_cost', 'sell_price', 'start_at', 'created_by', 'created_at', 'actions'];
    public readonly manufacturerDisplayedColumns: string[] = ['manufacturer', 'specification_sheet_count', 'actions'];
    public productManufacturers: MatTableDataSource<ProductManufacturer> = new MatTableDataSource();
    public create: boolean = true;
    public productMeasures: any[] = [];
    public product: any = null;
    public productForm: FormGroup;

    public get displayedColumns(): string[]
    {
        return this.settingsService.isPricingEnabled()
            ? this.pricingDisplayedColumns
            : this.manufacturerDisplayedColumns;
    }

    public get pricingEnabled(): boolean
    {
        return this.settingsService.isPricingEnabled();
    }

    public get productManufacturerProducts(): ProductManufacturer[]
    {
        return this.product?.manufacturer_products ?? [];
    }

    public get isOverridden(): boolean
    {
        return !!this.product?.override;
    }

    public breadcrumbs: Breadcrumb[] = [
        {
            title: 'Home',
            url: '/app/',
            active: false
        },
        {
            title: 'Products',
            active: false,
            url: '/app/products/',
        },
        {
            title: 'Add Product',
            active: true,
        }
    ];

    public buttons: Button[] = [
        {
            type: 'button',
            material: 'flat',
            color: 'primary',
            title: 'Save Product',
            permission: 'add_products',
            callback: () => {
                this.onSubmit();
            }
        }
    ];

    public constructor(
        private dialog: MatDialog,
        private activatedRoute: ActivatedRoute,
        private apiService: ApiService,
        private router: Router,
        private fb: FormBuilder,
        private snackBar: MatSnackBar,
        private settingsService: SettingsService,
    )
    {
        this.productForm = this.fb.group({
            name: [null, Validators.required],
            status_id: [1, Validators.required],
            measure_id: [null, Validators.required],
            display_measure: [null]
        });
    }

    public ngOnInit(): void
    {
        this.apiService.get('products/measures?fields[product_measures]=id,unit,symbol').subscribe({
            next: result => this.productMeasures = result.data,
            error: error => this.apiService.handleError(error)
        });

        this.activatedRoute.params.subscribe(params => {
            if (params['id']) {
                this.create = false;
                this.getProductData(params['id']);
            }
        });
    }

    public onSubmit(): void
    {
        if (this.productForm.valid) {
            if (this.product) {
                this.apiService.update('products', this.product.id, this.productForm.value).subscribe({
                    next: () => this.router.navigateByUrl(`/app/products`),
                    error: error => this.apiService.handleError(error)
                });
            } else {
                this.apiService.create('products', this.productForm.value).subscribe({
                    next: result => this.router.navigateByUrl(`/app/products/edit/${result.data.id}`),
                    error: error => this.apiService.handleError(error)
                });
            }
        }
    }

    public pricingPage(manufacturer = null): void
    {
        this.router.navigateByUrl(`/app/products/edit/${this.product.id}/pricing/${manufacturer?.id ?? manufacturer?.default_price?.manufacturer_product_id ?? 'create'}`, {
            state: {
                product: this.product,
                price: manufacturer?.default_price
            }
        });
    }

    public onArchive(id): void
    {
        this.apiService.delete(`products/manufacturer`, id).subscribe({
            next: () => this.getProductData(this.product.id),
            error: error => {
                this.apiService.handleError(error.message);
                this.snackBar.open('You cannot delete a manufacturer product link that is in use on existing sites.', 'Ok.', {duration: 4000});
            }
        });
    }

    public confirmArchiveManufacturerFromProduct(id: number): void
    {
        const confirmDialogRef = this.dialog.open(DialogComponent, {
            data: {
                title: 'Archive Manufacturer',
                message: 'Are you sure you want to archive this manufacturer from this product? You can\'t undo this action.',
                type: 'danger-outline',
                confirmButtonLabel: 'Archive'
            }
        });

        confirmDialogRef.afterClosed().subscribe(confirm => {
            if (confirm) {
                this.onArchive(id);
            }
        });
    }

    public getProductData(id): void
    {
        const url = query(`products/${id}`).build();
        this.apiService.get(url).subscribe({
            next: result => this.hydrateProduct(result.data),
            error: error => this.apiService.handleError(error)
        });
    }

    public getManufacturerProductLabel(manufacturerProduct: ProductManufacturer): string
    {
        return manufacturerProduct?.manufacturer?.name ?? `Manufacturer Link #${manufacturerProduct?.id}`;
    }

    public getManufacturerProductSpecificationSheetCount(manufacturerProduct: ProductManufacturer): number
    {
        return manufacturerProduct?.specification_sheets?.length ?? 0;
    }

    public uploadPrices(manufacturer): void
    {
        const priceUploadDialog: MatDialogRef<PriceUploadModalComponent> = this.dialog.open(PriceUploadModalComponent, {
            width: '65%',
            panelClass: 'mobile-width',
            disableClose: true,
            data: {
                price: manufacturer?.default_price,
                product: this.product,
                manufacturerProductId: manufacturer?.default_price?.manufacturer_product_id,
            }
        });

        priceUploadDialog.afterClosed().subscribe((): void => {});
    }

    public resetToDefaults(): void
    {
        if (!this.isOverridden) return;

        const confirmDialogRef = this.dialog.open(DialogComponent, {
            width: '400px',
            data: {
                title: 'Reset to System Defaults',
                message: 'Are you sure you want to reset this product to the system defaults?',
                type: 'danger',
                confirmButtonLabel: 'Reset Fields'
            }
        });

        confirmDialogRef.afterClosed().subscribe(confirm => {
            if (confirm) {
                this.apiService.delete('products', `${this.product.id}/override`).subscribe({
                    next: (result: any) => {
                        this.hydrateProduct(result.data);
                        this.snackBar.open('Product reset to system defaults and saved.', 'Done', {duration: 4000});
                    },
                    error: (error) => this.apiService.handleError(error)
                });
            }
        });
    }

    protected hydrateProduct(product: any): void
    {
        this.product = product;
        this.productManufacturers = new MatTableDataSource(this.productManufacturerProducts);

        this.productForm = this.fb.group({
            name: [this.product.name, Validators.required],
            status_id: [this.product.status?.id ?? this.product.status_id ?? 1, Validators.required],
            measure_id: [this.product.measure?.id ?? null, Validators.required],
            display_measure: [this.product.display_measure],
        });

        this.breadcrumbs = [
            {
                title: 'Home',
                url: '/app/',
                active: false
            },
            {
                title: 'Products',
                active: false,
                url: '/app/products/',
            },
            {
                title: `${this.product.name}`,
                active: true,
            }
        ];

        this.buttons = [
            {
                type: 'button',
                material: 'flat',
                color: 'primary',
                title: 'Save Product',
                permission: 'add_products',
                callback: () => {
                    this.onSubmit();
                }
            }
        ];

        if (this.isOverridden) {
            this.buttons.unshift({
                type: 'button',
                material: 'stroked',
                color: 'warn',
                title: 'Reset',
                permission: 'edit_products',
                callback: () => {
                    this.resetToDefaults();
                }
            });
        }
    }
}
